import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Link2, RefreshCw, Trash2 } from "lucide-react";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { Modal } from "../Modal";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { investorFounderAPI } from "@/services/api/investor.service";
import { ApiError } from "@/services/api/base.service";

interface InvestorLinksPanelProps {
  startupId: string;
}

function formatDateTime(value: string | null): string {
  if (!value) return "Never";
  return new Date(value).toLocaleString();
}

export const InvestorLinksPanel: React.FC<InvestorLinksPanelProps> = ({ startupId }) => {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [passcode, setPasscode] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pinLinkId, setPinLinkId] = useState<string | null>(null);
  const [newPin, setNewPin] = useState("");
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const { data: links = [], isLoading } = useQuery({
    queryKey: ["investorLinks", startupId],
    queryFn: () => investorFounderAPI.listLinks(startupId),
    enabled: Boolean(startupId),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      investorFounderAPI.createLink(startupId, {
        label: label.trim(),
        passcode: passcode.trim(),
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      }),
    onSuccess: async (result) => {
      setLabel("");
      setPasscode("");
      setExpiresAt("");
      setError(null);
      await navigator.clipboard.writeText(result.url);
      setCopyFeedback("Link created and copied to clipboard.");
      queryClient.invalidateQueries({ queryKey: ["investorLinks", startupId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const revokeMutation = useMutation({
    mutationFn: (linkId: string) => investorFounderAPI.revokeLink(startupId, linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["investorLinks", startupId] });
    },
  });

  const pinMutation = useMutation({
    mutationFn: ({ linkId, pin }: { linkId: string; pin: string }) =>
      investorFounderAPI.updateLinkPin(startupId, linkId, pin),
    onSuccess: () => {
      setPinLinkId(null);
      setNewPin("");
      setCopyFeedback("PIN updated.");
    },
    onError: (err: Error) => setError(err.message),
  });

  const rotateMutation = useMutation({
    mutationFn: (linkId: string) => investorFounderAPI.rotateLinkToken(startupId, linkId),
    onSuccess: async (result) => {
      await navigator.clipboard.writeText(result.url);
      setCopyFeedback("New link URL generated and copied. Previous URLs for this link no longer work.");
      queryClient.invalidateQueries({ queryKey: ["investorLinks", startupId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const copyLink = async (linkId: string, hasStoredToken?: boolean) => {
    setError(null);
    if (hasStoredToken === false) {
      const confirmed = window.confirm(
        "This link was created before copy-anytime was available. Generate a new URL? The previous URL will stop working.",
      );
      if (confirmed) {
        rotateMutation.mutate(linkId);
      }
      return;
    }

    try {
      const { url } = await investorFounderAPI.getShareUrl(startupId, linkId);
      await navigator.clipboard.writeText(url);
      setCopyFeedback("Link copied to clipboard.");
    } catch (err) {
      if (
        err instanceof ApiError &&
        (err.code === "TOKEN_NOT_STORED" || err.code === "TOKEN_DECRYPT_FAILED")
      ) {
        const confirmed = window.confirm(
          `${err.message}\n\nGenerate a new URL now? The previous URL will stop working.`,
        );
        if (confirmed) {
          rotateMutation.mutate(linkId);
        }
        return;
      }
      setError(err instanceof Error ? err.message : "Could not copy link.");
    }
  };

  return (
    <SettingsSectionCard
      title="Investor share links"
      description="Create password-protected read-only links. Copy and re-share anytime, or update the PIN."
    >
      <div className="mb-6 space-y-4 rounded-[14px] border border-white/10 p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Link label">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Seed round — Dec 2025"
            />
          </FormField>
          <FormField label="6-digit PIN (required)">
            <Input
              value={passcode}
              onChange={(e) => setPasscode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
            />
          </FormField>
        </div>
        <FormField label="Expiration (optional)">
          <Input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </FormField>
        {error ? <div className="text-sm text-danger">{error}</div> : null}
        {copyFeedback ? (
          <div className="text-sm text-brand">{copyFeedback}</div>
        ) : null}
        <Button
          type="button"
          variant="primary"
          size="md"
          disabled={
            createMutation.isPending ||
            !label.trim() ||
            passcode.length !== 6
          }
          onClick={() => createMutation.mutate()}
        >
          <Link2 className="mr-1 h-4 w-4" />
          Create link
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-white/55">Loading links...</div>
      ) : links.length === 0 ? (
        <div className="text-sm text-white/55">No investor links yet.</div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <div
              key={link.id}
              className="rounded-[14px] border border-white/10 bg-surface/40 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white">{link.label}</div>
                  <div className="mt-1 text-xs text-white/50">
                    Created {formatDateTime(link.createdAt)}
                    {link.expiresAt ? ` · Expires ${formatDateTime(link.expiresAt)}` : ""}
                    {link.revokedAt ? " · Revoked" : ""}
                  </div>
                  <div className="mt-2 text-xs text-white/45">
                    {link.viewCount} views · Last seen {formatDateTime(link.lastViewedAt)}
                  </div>
                  {link.uniqueViewerNames?.length ? (
                    <div className="mt-1 text-xs text-white/45">
                      Viewers: {link.uniqueViewerNames.join(", ")}
                    </div>
                  ) : null}
                  {link.hasStoredToken === false ? (
                    <div className="mt-2 text-xs text-amber-300/90">
                      Copy unavailable — regenerate URL to enable re-sharing.
                    </div>
                  ) : null}
                </div>
                {!link.revokedAt ? (
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      title="Copy link"
                      onClick={() => copyLink(link.id, link.hasStoredToken)}
                      className="rounded-lg p-2 text-white/55 hover:bg-white/10 hover:text-white"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Regenerate URL"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Generate a new URL for "${link.label}"? The previous URL will stop working.`,
                          )
                        ) {
                          rotateMutation.mutate(link.id);
                        }
                      }}
                      className="rounded-lg p-2 text-white/55 hover:bg-white/10 hover:text-white"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Change PIN"
                      onClick={() => {
                        setPinLinkId(link.id);
                        setNewPin("");
                      }}
                      className="rounded-lg p-2 text-white/55 hover:bg-white/10 hover:text-white"
                    >
                      <KeyRound className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Revoke link"
                      onClick={() => {
                        if (window.confirm(`Revoke link "${link.label}"?`)) {
                          revokeMutation.mutate(link.id);
                        }
                      }}
                      className="rounded-lg p-2 text-white/55 hover:bg-danger/10 hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={Boolean(pinLinkId)}
        onClose={() => setPinLinkId(null)}
        title="Change PIN"
      >
        <div className="space-y-4">
          <FormField label="New 6-digit PIN">
            <Input
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" size="md" onClick={() => setPinLinkId(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={pinMutation.isPending || newPin.length !== 6}
              onClick={() => {
                if (pinLinkId) {
                  pinMutation.mutate({ linkId: pinLinkId, pin: newPin });
                }
              }}
            >
              {pinMutation.isPending ? "Saving..." : "Update PIN"}
            </Button>
          </div>
        </div>
      </Modal>
    </SettingsSectionCard>
  );
};
